import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { getBlogPostCardImage, getBlogPostImagePlaceholder, listPosts } from "@/content/blog";
import { BlogCardImage } from "@/components/BlogCardImage";
import { trackRelatedArticleClick } from "@/lib/analytics";

type RelatedNewsProps = {
  /** Preferred slugs in priority order. Missing entries are dropped silently. */
  slugs?: string[];
  /** How many articles to show. */
  count?: number;
  /** Section heading. */
  title?: string;
  /** Section eyebrow label. */
  eyebrow?: string;
  /** Slugs to exclude from fallback fill (e.g. current article). */
  exclude?: string[];
};

export function RelatedNews({
  slugs,
  count = 10,
  title = "Latest news",
  eyebrow = "From the blog",
  exclude = [],
}: RelatedNewsProps) {
  const all = listPosts();
  const bySlug = new Map(all.map((p) => [p.slug, p]));
  const excludeSet = new Set(exclude);

  const picked: typeof all = [];
  const seen = new Set<string>();

  // 1. curated slugs first
  for (const slug of slugs ?? []) {
    if (picked.length >= count) break;
    if (seen.has(slug) || excludeSet.has(slug)) continue;
    const post = bySlug.get(slug);
    if (post) {
      picked.push(post);
      seen.add(slug);
    }
  }

  // 2. fill with latest posts
  for (const post of all) {
    if (picked.length >= count) break;
    if (seen.has(post.slug) || excludeSet.has(post.slug)) continue;
    picked.push(post);
    seen.add(post.slug);
  }

  if (picked.length === 0) return null;

  return <RelatedNewsCarousel picked={picked} title={title} eyebrow={eyebrow} />;
}

type Post = ReturnType<typeof listPosts>[number];

function RelatedNewsCarousel({
  picked,
  title,
  eyebrow,
}: {
  picked: Post[];
  title: string;
  eyebrow: string;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const updateEdges = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateEdges();
    el.addEventListener("scroll", updateEdges, { passive: true });
    const ro = new ResizeObserver(updateEdges);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateEdges);
      ro.disconnect();
    };
  }, [updateEdges]);

  const scrollByCard = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>("[data-carousel-card]");
    const track = el.querySelector<HTMLElement>(".related-news-track");
    const gap = track ? Number.parseFloat(getComputedStyle(track).columnGap) || 0 : 0;
    const step = card
      ? card.offsetWidth + gap
      : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section className="border-t border-border bg-card/60">
      <div className="container mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div className="min-w-0">
            <p className="demo-section-label">{eyebrow}</p>
            <h2 className="site-heading-md mt-4 font-display font-light">{title}</h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => scrollByCard(-1)}
              disabled={!canPrev}
              aria-label="Previous articles"
              className="grid h-11 w-11 place-items-center rounded-full border border-border bg-background text-foreground transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-border disabled:hover:bg-background disabled:hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => scrollByCard(1)}
              disabled={!canNext}
              aria-label="Next articles"
              className="grid h-11 w-11 place-items-center rounded-full border border-border bg-background text-foreground transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-border disabled:hover:bg-background disabled:hover:text-foreground"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
            <Link
              to="/blog"
              className="demo-link-arrow-button inline-flex items-center gap-2 text-sm font-semibold ml-2"
            >
              View all
              <span className="demo-arrow-circle demo-arrow-small">
                <ArrowUpRight className="h-4 w-4" />
              </span>
            </Link>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="related-news-scroller -mx-6 px-6 lg:-mx-10 lg:px-10"
        >
          <ul className="related-news-track list-none p-0 m-0">
            {picked.map((p, index) => {
              const displayTitle = p.title
                .replace(/\s*(?:\||—|-)\s*Phil Carr(?:\s+SEO Consultant)?(?:\s*&\s*Web Strategist)?\s*$/i, "")
                .trim();
              return (
                <li
                  key={p.slug}
                  data-carousel-card
                  className="related-news-card"
                >
                  <Link
                    to="/blog/$slug"
                    params={{ slug: p.slug }}
                    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-background text-decoration-none transition-colors hover:border-primary"
                    data-analytics-slug={p.slug}
                    data-analytics-position={index + 1}
                    onClick={() =>
                      trackRelatedArticleClick({
                        slug: p.slug,
                        title: displayTitle,
                        position: index + 1,
                        section: title,
                        source_path:
                          typeof window !== "undefined"
                            ? window.location.pathname
                            : "",
                      })
                    }
                  >
                    <BlogCardImage
                      src={getBlogPostCardImage(p)}
                      placeholderSrc={getBlogPostImagePlaceholder(p)}
                      alt={`${displayTitle} featured image`}
                      className="related-news-card-image"
                    />
                    <div className="flex flex-1 flex-col p-6">
                      {p.date && (
                        <p className="m-0 text-xs uppercase tracking-widest text-muted-foreground">
                          <time dateTime={p.date}>
                            {new Date(p.date).toLocaleDateString("en-GB", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              timeZone: "UTC",
                            })}
                          </time>
                        </p>
                      )}
                      <h3 className="mt-3 font-display text-lg leading-snug line-clamp-3 text-foreground">
                        {displayTitle}
                      </h3>
                      <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                        {p.description}
                      </p>
                      <span className="mt-auto pt-6 inline-flex items-center gap-2 text-sm font-semibold text-foreground">
                        Read article
                        <span className="grid h-8 w-8 place-items-center rounded-full border border-border transition-colors group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground">
                          <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
                        </span>
                      </span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
